import { ChallengeUrls } from "@/modules/auth/common/constants";
import type { ILoginWithOtpRequest, LoginWithOtpResponse, } from "@/modules/auth/common/types";
import { useLoginWithOtpMutation } from "@/modules/auth/services";
import { setChallenge, setIsAuth, setVerificationId } from "@/modules/auth/state";
import { LOGIN_OTP_COUNT, OTP_PATTERN } from "@/shared/common/constants";
import { AppPaths, ErrorMessages, Storage } from "@/shared/common/enums";
import { Notification } from "@/shared/components";
import { getErrorMessage, setToLocalStorage, setUserToStorage } from "@/shared/helpers";
import { useAppDispatch, useAppSelector, useNotify } from "@/shared/hooks";
import { Button } from "@ab.uitools/ui-library/components/Button";
import { Heading } from "@ab.uitools/ui-library/components/Heading";
import { OneTimePassword } from "@ab.uitools/ui-library/components/OneTimePassword";
import { Text } from "@ab.uitools/ui-library/components/Text";
import { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { useNavigate } from "react-router-dom";

export const TwoFactor = () => {
  const { t } = useTranslation();
  const { showErrorAlertNotification, clearAlertNotification } = useNotify();
  const dispatch = useAppDispatch();
  const verificationId = useAppSelector((state) => state.auth.verificationId);
  const navigate = useNavigate();
  const [otp, setOtp] = useState<string>("");
  const [otpError, setOtpError] = useState<string>("");
  const [loginWitOtp, { isLoading }] = useLoginWithOtpMutation();

  const handleChangeOtp = (value: string) => {
    setOtp(value);
    if (otpError) setOtpError("");
  };

  const handleSubmit = () => {
    if (otp.length !== LOGIN_OTP_COUNT) {
      setOtpError(t("errorMessages.enterFullPassword"));
      return;
    }

    const data: ILoginWithOtpRequest = {
      otp,
      authenticationId: verificationId,
    };

    loginWitOtp(data)
      .unwrap()
      .then((res: LoginWithOtpResponse) => {
        const user = res?.user;
        const challenge = res?.challenge as keyof typeof ChallengeUrls;
        const verificationId = res?.id;

        if (!challenge && user) {
          setUserToStorage(user);
          setToLocalStorage(Storage.IsAuth, "true");
          dispatch(setIsAuth(true));
          navigate(AppPaths.Dashboard);
        } else {
          dispatch(setVerificationId(verificationId));
          dispatch(setChallenge(challenge));
          if (ChallengeUrls[challenge]) {
            navigate(ChallengeUrls[challenge]);
          }
        }
      })
      .catch((err) => {
        const message = t(getErrorMessage(err, ErrorMessages.TechnicalDifficulties));
        setOtpError(message);
        showErrorAlertNotification(err);
      });
  };

  useEffect(() => {
    clearAlertNotification();
    if (otp.length === LOGIN_OTP_COUNT) {
      handleSubmit();
    }
  }, [otp]);

  return (
    <div data-testid="two-factor-form" className="auth">
      <form
        onSubmit={(e) => {
          e.preventDefault();
          handleSubmit();
        }}
        className="card flexbox flex-direction--column align-items--center full-width p-48"
      >
        <Heading
          className="auth-title"
          size="xsmall"
          weight="bold"
          lineHeight="small"
        >
          {t("auth.twoFactor.verification")}
        </Heading>
        <Notification className="mt-8 full-width"/>
        <Text
          size="large"
          type="secondary"
          className="mt-24 mb-16 enter-otp-text"
          lineHeight="large"
        >
          {t("auth.twoFactor.enterOTP")}
        </Text>
        <OneTimePassword
          count={LOGIN_OTP_COUNT}
          error={otpError}
          placeholder="X"
          required
          type="number"
          pattern={OTP_PATTERN}
          autoFocus
          label={t("auth.twoFactor.oneTimeCode")}
          hasError={!!otpError}
          onInvalid={(e) => e.preventDefault()}
          handleChange={(_, value) => handleChangeOtp(value)}
        />
        <Button
          className="full-width mt-32"
          buttonActionType="submit"
          buttonText={t("auth.twoFactor.action")}
          isLoading={isLoading}
          disabled={isLoading}
          data-testid="auth-otp-button"
          data-ga-id="auth-otp-button"
          data-ga-action="Auth with otp"
        />
      </form>
    </div>
  );
};
